import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ObjectHover } from './ObjectHover';

export default function History({ history }) {
  const ref = useRef();
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo(0, ref.current.scrollHeight);
    }
  }, [history]);

  if (!history) {
    return null;
  }

  return (
    <div
      style={{ width: '100%', height: '100%', overflow: 'scroll' }}
      ref={ref}
    >
      {history.map((item, index) => (
        <HistoryItem key={index} item={item} />
      ))}
    </div>
  );
}

const Item = styled.div`
  margin: 4px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid gray;
  transition: transform 200ms ease-in-out;
  cursor: pointer;

  :hover {
    transform: translate(-1px, -1px);
  }
`;

function HistoryItem({ item }) {
  if (item.status !== 'running') {
    // filter out 'ready' state
    return null;
  }
  return (
    <Item>
      <Logs logs={item.logs} />
      {item.statementIndex < 0
        ? item.isComponentDirty
          ? `${item.componentName} has been scheduled for a re-render`
          : `${item.componentName} is ready to be rendered`
        : null}
    </Item>
  );
}

function Logs({ logs }) {
  if (!logs || !Array.isArray(logs)) {
    return null;
  }
  return logs.map((log, index) => <Log key={index} log={log} />);
}

const HookBadge = styled.div`
  display: inline-block;
  border-radius: 4px;
  color: white;
  background-color: slateblue;
  padding: 1px 4px;
  font-weight: 500;
  font-size: 0.8em;
  margin-right: 4px;
`;
const Badge = styled.div`
  display: inline-block;
  border-radius: 4px;
  color: white;
  background-color: ${props => props.color};
  padding: 1px 4px;
  font-weight: 500;
  font-size: 0.8em;
  margin-right: 4px;
`;

const LOG_MAP = {
  error: LogError,
  if: LogIf,
  assignment: LogAssignment,
  render: LogRender,
  updateProps: LogProps,
  updateContext: LogContext,

  'hooks/useState': LogHookUseState,
  'update/useState': LogUpdateUseState,

  'hooks/useReducer': LogHookUseReducer,
  'update/useReducer': LogUpdateUseReducer,

  'hooks/useCallback': LogHookUseCallback,
  'update/useCallback': LogUpdateUseCallback,

  'hooks/useMemo': LogHookUseMemo,
  'update/useMemo': LogUpdateUseMemo,

  'hooks/useContext': LogHookUseContext,
  'update/useContext': LogUpdateUseContext,

  'hooks/useEffect': LogHookUseEffect,
  'update/useEffect': LogHookUpdateEffect,
  'effect/flush': LogFlushEffect,
};

function Log({ log }) {
  const LogComponent = LOG_MAP[log.type];
  if (LogComponent) {
    return <LogComponent log={log} />;
  }
  return null;
}

function LogError({ log }) {
  return (
    <div>
      <Badge color="red">Error</Badge>
      {String(log.error)}
    </div>
  );
}

function LogIf({ log }) {
  return (
    <div>
      <Badge color="violet">If</Badge>
      Testing If Condition
    </div>
  );
}

function LogAssignment({ log }) {
  return (
    <div>
      Assigning <ObjectHover data={log.value} /> to {log.id}
    </div>
  );
}

function LogRender({ log }) {
  return (
    <div>
      Return a virtual DOM.{' '}
      <a
        href="https://reactjs.org/docs/reconciliation.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        Reconcile
      </a>{' '}
      and render to DOM.
    </div>
  );
}

function LogProps({ log }) {
  return (
    <div>
      <HookBadge>props</HookBadge>
      Updated props {log.key} to <ObjectHover data={log.value} />. Schedule a
      re-render.
    </div>
  );
}

function LogContext({ log }) {
  return (
    <div>
      <HookBadge>context</HookBadge>
      Updated context value <ObjectHover data={log.context} />. Schedule a
      re-render.
    </div>
  );
}

function LogHookUseState({ log }) {
  return (
    <div>
      <HookBadge>useState</HookBadge>
      {log.isInitial ? (
        <>
          Creating new <em>useState</em> hook with initial state{' '}
          <ObjectHover data={log.state} />
        </>
      ) : (
        <>
          Reusing <em>useState</em> hook, with state{' '}
          <ObjectHover data={log.state} /> and setState{' '}
          <ObjectHover data={log.setState} />{' '}
        </>
      )}
    </div>
  );
}

function LogUpdateUseState({ log }) {
  return (
    <div>
      <HookBadge>useState</HookBadge>
      Updated <ObjectHover data={log.oldValue} /> to{' '}
      <ObjectHover data={log.newValue} />
    </div>
  );
}

function LogHookUseReducer({ log }) {
  return (
    <div>
      <HookBadge>useReducer</HookBadge>
      {log.isInitial ? (
        <>
          Creating new <em>useReducer</em> hook with initial state{' '}
          <ObjectHover data={log.state} /> (
          {log.init ? (
            <>
              Generated by calling <ObjectHover data={log.init} /> with{' '}
              <ObjectHover data={log.initialArg} />
            </>
          ) : (
            'Passed as 2nd argument in useReducer'
          )}
        </>
      ) : (
        <>
          Reusing <em>useReducer</em> hook, with state{' '}
          <ObjectHover data={log.state} /> and dispatch{' '}
          <ObjectHover data={log.dispatch} />
        </>
      )}
    </div>
  );
}

function LogUpdateUseReducer({ log }) {
  return (
    <div>
      <HookBadge>useReducer</HookBadge>
      Updated to new state <ObjectHover data={log.nextState} /> (return value
      from calling reducer <ObjectHover data={log.reducer} /> with{' '}
      <ObjectHover data={log.oldState} /> and <ObjectHover data={log.action} />)
    </div>
  );
}

function LogHookUseCallback({ log }) {
  return (
    <>
      <div>
        <HookBadge>useCallback</HookBadge>
        Creating new <em>useCallback</em> hook, with deps{' '}
        <Deps deps={log.deps} />
      </div>
      <div>
        <HookBadge>useCallback</HookBadge>
        Returning <ObjectHover data={log.callback} />
      </div>
    </>
  );
}

function LogUpdateUseCallback({ log }) {
  return (
    <>
      <div>
        <HookBadge>useCallback</HookBadge>
        Reusing <em>useCallback</em> hook
      </div>
      <div>
        <HookBadge>useCallback</HookBadge>
        Comparing <Deps deps={log.prevDeps} /> with <Deps deps={log.deps} />
      </div>
      {log.shouldUpdate && (
        <div>
          <HookBadge>useCallback</HookBadge>
          Deps changed. Discarding <ObjectHover data={log.prevCallback} />
        </div>
      )}
      <div>
        <HookBadge>useCallback</HookBadge>
        Returning <ObjectHover data={log.callback} />
      </div>
    </>
  );
}

function LogHookUseMemo({ log }) {
  return (
    <>
      <div>
        <HookBadge>useMemo</HookBadge>
        Creating new <em>useMemo</em> hook, with deps <Deps deps={log.deps} />
      </div>
      <div>
        <HookBadge>useMemo</HookBadge>
        Called <ObjectHover data={log.memo} />
      </div>
      <div>
        <HookBadge>useMemo</HookBadge>
        Returning <ObjectHover data={log.memoised} />
      </div>
    </>
  );
}

function LogUpdateUseMemo({ log }) {
  return (
    <>
      <div>
        <HookBadge>useMemo</HookBadge>
        Reusing <em>useMemo</em> hook
      </div>
      <div>
        <HookBadge>useMemo</HookBadge>
        Comparing <Deps deps={log.prevDeps} /> with <Deps deps={log.deps} />
      </div>
      {log.shouldUpdate && (
        <>
          <div>
            <HookBadge>useMemo</HookBadge>
            Deps changed. Discarding <ObjectHover data={log.prevMemoised} />
          </div>
          <div>
            <HookBadge>useMemo</HookBadge>
            Called <ObjectHover data={log.memo} />
          </div>
        </>
      )}
      <div>
        <HookBadge>useMemo</HookBadge>
        Returning <ObjectHover data={log.memoised} />
      </div>
    </>
  );
}

function Deps({ deps }) {
  return (
    <>
      {'['}
      {deps.map((dep, index) => (
        <React.Fragment key={index}>
          {index > 0 && ','}
          <ObjectHover data={dep} />
        </React.Fragment>
      ))}
      {']'}
    </>
  );
}

function LogHookUseContext({ log }) {
  return (
    <>
      <div>
        <HookBadge>useContext</HookBadge>
        Creating new <em>useContext</em> hook
      </div>
      <div>
        <HookBadge>useContext</HookBadge>
        Return context value <ObjectHover data={log.context.value} />
      </div>
    </>
  );
}

function LogUpdateUseContext({ log }) {
  return (
    <>
      <div>
        <HookBadge>useContext</HookBadge>
        Reusing <em>useContext</em> hook
      </div>
      <div>
        <HookBadge>useContext</HookBadge>
        Return context value <ObjectHover data={log.context.value} />
      </div>
    </>
  );
}

function LogHookUseEffect({ log }) {
  return (
    <>
      <div>
        <HookBadge>useEffect</HookBadge>
        Creating new <em>useEffect</em> hook, with deps <Deps deps={log.deps} />
      </div>
      <div>
        <HookBadge>useEffect</HookBadge>
        Scheduled <ObjectHover data={log.callback} /> after component has
        mounted.
      </div>
    </>
  );
}

function LogHookUpdateEffect({ log }) {
  return (
    <>
      <div>
        <HookBadge>useEffect</HookBadge>
        Comparing <Deps deps={log.prevDeps} /> with <Deps deps={log.deps} />
      </div>
      {log.shouldUpdate ? (
        <>
          <div>
            <HookBadge>useEffect</HookBadge>
            Deps changed.
          </div>
          <div>
            <HookBadge>useEffect</HookBadge>
            Scheduled{' '}
            {log.prevDestructure && (
              <>
                cleanup <ObjectHover data={log.prevDestructure} /> then{' '}
              </>
            )}
            <ObjectHover data={log.callback} /> after component has updated.
          </div>
        </>
      ) : (
        <div>
          <HookBadge>useEffect</HookBadge>
          No changed.
        </div>
      )}
    </>
  );
}

function LogFlushEffect({ log }) {
  return (
    <>
      {log.prevCleanup && (
        <div>
          <HookBadge>useEffect</HookBadge>
          Execute cleanup: <ObjectHover data={log.prevCleanup} />
        </div>
      )}
      <div>
        <HookBadge>useEffect</HookBadge>
        Execute effect: <ObjectHover data={log.effect.callback} />
      </div>
    </>
  );
}
